// src/components/ReferralPanel.tsx
// Consolidated referral system component with cleaner state management
// Replaces the fragmented referral logic in Dashboard.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { getSupabase, currentUser } from "@/lib/supabase";
import { 
  Gift, Copy, Check, Share2, Users, 
  ChevronRight, Sparkles, Trophy, Loader2,
  MessageCircle
} from 'lucide-react';

// ============================================
// TYPES & CONSTANTS
// ============================================

const REFERRAL_REWARDS = [
  { count: 1, key: "credit_5", label: "£5 Account Credit", description: "Applied to your next invoice", icon: "💷" },
  { count: 3, key: "free_month", label: "Free Month", description: "£12.99 subscription credit", icon: "🎁" },
  { count: 5, key: "training_program", label: "Training Program", description: "Full Input training plan", icon: "📋" },
  { count: 10, key: "rmt_test", label: "Free RMR Test", description: "Resting Metabolic Rate assessment", icon: "🔬" },
] as const;

interface ReferralState {
  loading: boolean;
  inviteLink: string;
  referralCount: number;
  unlockedRewards: string[];
  nextTier: { count: number; key: string } | null;
  progressPct: number;
  newlyUnlocked: string[];
  error: string | null;
}

// ============================================
// CUSTOM HOOK: useReferralSystem
// ============================================

function useReferralSystem() {
  const [state, setState] = useState<ReferralState>({
    loading: true,
    inviteLink: '',
    referralCount: 0,
    unlockedRewards: [],
    nextTier: null,
    progressPct: 0,
    newlyUnlocked: [],
    error: null,
  });

  const checkRewards = useCallback(async () => {
    try {
      const user = await currentUser();
      if (!user) return;

      const supabase = getSupabase();
      const { data: session } = await supabase.auth.getSession();

      // Call the consolidated edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-and-apply-referral-rewards`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to check rewards');
      }

      const data = await response.json();

      setState(prev => ({
        ...prev,
        referralCount: data.referralCount,
        unlockedRewards: data.unlockedRewards,
        nextTier: data.nextTier,
        progressPct: data.progressPct,
        newlyUnlocked: data.newlyUnlocked || [],
        loading: false,
      }));

      // Show notification for newly unlocked rewards
      if (data.newlyUnlocked?.length > 0) {
        const rewardNames = data.newlyUnlocked
          .map((key: string) => REFERRAL_REWARDS.find(r => r.key === key)?.label)
          .filter(Boolean)
          .join(', ');
        
        // You could replace this with a toast notification
        console.log(`🎉 Unlocked: ${rewardNames}`);
      }

    } catch (err: any) {
      console.error('Referral check error:', err);
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  }, []);

  const loadInviteLink = useCallback(async () => {
    try {
      const user = await currentUser();
      if (!user) return;

      const { data } = await getSupabase()
        .from("clients")
        .select("referral_code")
        .eq("user_id", user.id)
        .single();

      if (data?.referral_code) {
        const link = `${window.location.origin}/signup?ref=${data.referral_code}`;
        setState(prev => ({ ...prev, inviteLink: link }));
      }
    } catch (err) {
      console.error('Failed to load invite link:', err);
    }
  }, []);

  useEffect(() => {
    loadInviteLink();
    checkRewards();
  }, [loadInviteLink, checkRewards]);

  return { ...state, refresh: checkRewards };
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function ReferralPanel() {
  const {
    loading,
    inviteLink,
    referralCount,
    unlockedRewards,
    nextTier,
    progressPct,
    newlyUnlocked,
    refresh,
  } = useReferralSystem();

  const [copied, setCopied] = useState(false);
  const [showAllRewards, setShowAllRewards] = useState(false);

  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    if (!inviteLink) return;
    const message = encodeURIComponent(
      `I've been using Input for my training & nutrition.\n\nJoin using my invite link 👇\n${inviteLink}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const shareNative = async () => {
    if (!inviteLink || !navigator.share) return;
    
    try {
      await navigator.share({
        title: 'Join Input',
        text: "I've been using Input for my training & nutrition. Join using my invite link!",
        url: inviteLink,
      });
    } catch (err) {
      // User cancelled or share failed
      console.log('Share cancelled');
    }
  };

  if (loading) {
    return (
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-gray-800 rounded w-1/3 mb-4" />
        <div className="h-10 bg-gray-800 rounded mb-3" />
        <div className="h-10 bg-gray-800 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Invite Link Card */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-green-400" />
          <h3 className="font-semibold text-[#0f172a]">Invite Friends</h3>
        </div>
        
        <p className="text-sm text-gray-500 mb-4">
          Share your link and earn rewards when friends join!
        </p>

        {/* Invite Link Display */}
        <div className="bg-[#f1f5f9] rounded-xl p-3 mb-3 group hover:border-green-500/30 border border-transparent transition">
          <p className="text-xs font-mono text-green-400 break-all select-all">
            {inviteLink || "Generating..."}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={copyLink}
            disabled={!inviteLink}
            className={`flex-1 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-medium
              ${inviteLink
                ? "bg-green-500 hover:bg-green-400 text-[#0f172a]"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"}
            `}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Link
              </>
            )}
          </button>

          <button
            onClick={shareWhatsApp}
            disabled={!inviteLink}
            className={`px-4 py-2.5 rounded-xl transition-all flex items-center justify-center
              ${inviteLink
                ? "bg-[#25D366] hover:bg-[#a78bfa] text-[#0f172a]"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"}
            `}
            title="Share via WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
          </button>

          {navigator.share && (
            <button
              onClick={shareNative}
              disabled={!inviteLink}
              className={`px-4 py-2.5 rounded-xl transition-all flex items-center justify-center
                ${inviteLink
                  ? "bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a]"
                  : "bg-gray-700 text-gray-500 cursor-not-allowed"}
              `}
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Progress Card */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            <h3 className="font-semibold text-[#0f172a]">Your Progress</h3>
          </div>
          <span className="text-2xl font-bold text-green-400">
            {referralCount}
          </span>
        </div>

        <p className="text-sm text-gray-500 mb-3">
          {referralCount === 0 
            ? "Share your link to start earning rewards!"
            : `${referralCount} friend${referralCount !== 1 ? 's' : ''} joined using your link`}
        </p>

        {/* Progress Bar */}
        {nextTier && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{referralCount} referrals</span>
              <span>{nextTier.count} needed</span>
            </div>
            <div className="w-full h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {nextTier.count - referralCount} more to unlock{' '}
              <span className="text-[#0f172a] font-medium">
                {REFERRAL_REWARDS.find(r => r.key === nextTier.key)?.label}
              </span>
            </p>
          </div>
        )}

        {!nextTier && unlockedRewards.length === REFERRAL_REWARDS.length && (
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <Sparkles className="w-4 h-4" />
            <span>All rewards unlocked! 🎉</span>
          </div>
        )}
      </div>

      {/* Rewards Card */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <h3 className="font-semibold text-[#0f172a]">Rewards</h3>
          </div>
          {unlockedRewards.length > 0 && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              {unlockedRewards.length} unlocked
            </span>
          )}
        </div>

        <div className="space-y-2">
          {REFERRAL_REWARDS.map((reward, index) => {
            const isUnlocked = unlockedRewards.includes(reward.key);
            const isNew = newlyUnlocked.includes(reward.key);
            
            // Show first 3 rewards, or all if expanded
            if (!showAllRewards && index >= 3 && !isUnlocked) return null;

            return (
              <div
                key={reward.key}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isUnlocked 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-[#f1f5f9] border border-transparent'
                } ${isNew ? 'animate-pulse' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{reward.icon}</span>
                  <div>
                    <p className={`text-sm font-medium ${isUnlocked ? 'text-green-400' : 'text-[#0f172a]'}`}>
                      {reward.label}
                    </p>
                    <p className="text-xs text-gray-500">
                      {reward.count} referral{reward.count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {isUnlocked ? (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Unlocked
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500">
                      {Math.max(reward.count - referralCount, 0)} to go
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!showAllRewards && REFERRAL_REWARDS.length > 3 && (
          <button
            onClick={() => setShowAllRewards(true)}
            className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-[#0f172a] transition flex items-center justify-center gap-1"
          >
            View all rewards
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================
// COMPACT VERSION (for Dashboard sidebar)
// ============================================

export function ReferralPanelCompact() {
  const {
    loading,
    inviteLink,
    referralCount,
    unlockedRewards,
    nextTier,
    progressPct,
  } = useReferralSystem();

  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    if (!inviteLink) return;
    const message = encodeURIComponent(
      `I've been using Input for my training & nutrition.\n\nJoin using my invite link 👇\n${inviteLink}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  if (loading) {
    return (
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 animate-pulse">
        <div className="h-4 bg-gray-800 rounded w-1/2 mb-3" />
        <div className="h-8 bg-gray-800 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Invite Link */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <p className="text-sm text-gray-500 mb-2">Your Invite Link</p>
        <div className="bg-[#f1f5f9] rounded-xl p-2 md:p-3 mb-3">
          <p className="text-[10px] md:text-xs font-mono text-green-400 break-all">
            {inviteLink || "Generating..."}
          </p>
        </div>
        <button
          onClick={copyLink}
          disabled={!inviteLink}
          className={`w-full py-2 md:py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base
            ${inviteLink
              ? "bg-green-500 hover:bg-green-400 text-[#0f172a]"
              : "bg-gray-700 text-gray-500 cursor-not-allowed"}
          `}
        >
          {copied ? "Copied!" : "Copy Link"}
        </button>
        <button 
          onClick={shareWhatsApp}
          disabled={!inviteLink}
          className={`mt-2 md:mt-3 w-full py-2 md:py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm md:text-base
            ${inviteLink
              ? "bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a]"
              : "bg-[#f8fafc] text-gray-500 cursor-not-allowed"}
          `}
        >
          Share via WhatsApp
        </button>
      </div>

      {/* Progress */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6 space-y-3 md:space-y-4">
        <h3 className="text-sm font-medium text-gray-500">Referral Progress</h3>
        <div className="flex justify-between items-center">
          <p className="text-[#0f172a] text-base md:text-lg font-semibold">
            {referralCount} referral{referralCount !== 1 ? "s" : ""}
          </p>
          {nextTier && (
            <span className="text-[10px] md:text-xs text-gray-500">
              Next: {REFERRAL_REWARDS.find(r => r.key === nextTier.key)?.label}
            </span>
          )}
        </div>
        <div className="w-full h-2 md:h-3 bg-[#f1f5f9] rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        {nextTier ? (
          <p className="text-[10px] md:text-xs text-gray-500">
            {Math.max(nextTier.count - referralCount, 0)} more to unlock{" "}
            <span className="text-[#0f172a]">
              {REFERRAL_REWARDS.find(r => r.key === nextTier.key)?.label}
            </span>
          </p>
        ) : (
          <p className="text-xs text-green-400">
            🎉 You've unlocked all rewards!
          </p>
        )}
      </div>

      {/* Unlocked Rewards */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6 space-y-3 md:space-y-4">
        <h3 className="text-sm font-medium text-gray-500">Unlocked Rewards</h3>
        {unlockedRewards.length === 0 ? (
          <p className="text-xs text-gray-500">
            No rewards unlocked yet – keep sharing 💪
          </p>
        ) : (
          <div className="space-y-2 md:space-y-3">
            {unlockedRewards.map(key => {
              const reward = REFERRAL_REWARDS.find(r => r.key === key);
              if (!reward) return null;
              
              return (
                <div
                  key={key}
                  className="flex justify-between items-center bg-[#f1f5f9] rounded-xl px-3 md:px-4 py-2 md:py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs md:text-sm font-semibold text-[#0f172a] truncate">
                      {reward.label}
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500 truncate">
                      {reward.description}
                    </p>
                  </div>
                  <span className="text-[10px] md:text-xs font-medium text-green-400 ml-2 flex-shrink-0">
                    ✓ Applied
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
